import type { PortalRequestItemKind } from "./portal-requests.js"

/**
 * Upload by link, sign in to view.
 *
 * A visit to a client link without a session gets a write-only grant. With
 * it the visitor can see what is still open (titles and instructions only),
 * upload files and answer open items. Nothing that reads the client's
 * history, files, messages or earlier answers accepts the grant.
 */

/** The scope claim the grant carries. A session has no scope claim. */
export const portalLinkWriteScope = "link_write" as const

/** How long one grant lasts. A visitor who stays longer gets a new one when
    the page asks for the open items again. */
export const portalLinkGrantMinutes = 120

/** The kinds a link visitor answers. The others need a signed-in contact: a
    table or signature has its own flow, a transaction question shows rows
    from the client's books, and an action item names the client's systems. */
export const portalLinkAnswerKinds = [
  "text",
  "date",
  "choice",
  "yes_no",
  "amount",
  "checklist",
] as const satisfies readonly PortalRequestItemKind[]

export type PortalLinkItemAction = "upload" | "answer" | "sign_in"

/**
 * What a link visitor can do with one item that is waiting on the client.
 *
 * An answer is only taken when the item has none, or the firm sent it back.
 * A link visitor never replaces an answer a client gave, so an open link
 * cannot overwrite what a signed-in client wrote.
 */
export const portalLinkItemAction = (item: {
  kind: PortalRequestItemKind
  hasAnswer: boolean
  sentBack: boolean
}): PortalLinkItemAction => {
  if (item.kind === "document") return "upload"
  if (
    (portalLinkAnswerKinds as readonly PortalRequestItemKind[]).includes(
      item.kind
    ) &&
    (!item.hasAnswer || item.sentBack)
  )
    return "answer"
  return "sign_in"
}

/** Who did something with only the link, as the firm's activity shows it.
    Anything typed is labelled as not verified. */
export const portalLinkActorName = (
  uploader?: { name?: string | null; email?: string | null } | null
): string => {
  const name = uploader?.name?.trim()
  const email = uploader?.email?.trim()
  if (name && email) return `${name}, ${email} (not verified)`
  if (name || email) return `${name || email} (not verified)`
  return "Someone with the client link"
}
